import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Plus, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";

const num = (v) => parseFloat(v) || 0;
const fmtBRL = (v) => (v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const emptyItem = () => ({ description: "", quantity: 1, unit_value: 0, ipi: 0, total_value: 0 });

export const BalanceEntryDialog = ({ open, onOpenChange, clientId, entry, onSaved }) => {
  const [supplier, setSupplier] = useState("");
  const [supplierCnpj, setSupplierCnpj] = useState("");
  const [nfNumber, setNfNumber] = useState("");
  const [nfDate, setNfDate] = useState("");
  const [items, setItems] = useState([emptyItem()]);
  const [freight, setFreight] = useState(0);
  const [discount, setDiscount] = useState(0);
  const [icmsSt, setIcmsSt] = useState(0);
  const [totalValue, setTotalValue] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    if (open) {
      setSupplier(entry?.supplier || "");
      setSupplierCnpj(entry?.supplier_cnpj || "");
      setNfNumber(entry?.nf_number || "");
      setNfDate(entry?.nf_date || "");
      setItems(entry?.items?.length ? entry.items.map((i) => ({ ...i })) : [emptyItem()]);
      setFreight(entry?.freight || 0);
      setDiscount(entry?.discount || 0);
      setIcmsSt(entry?.icms_st || 0);
      setTotalValue(entry ? String(entry.total_value ?? "") : "");
      setNotes(entry?.notes || "");
    }
  }, [open, entry]);

  const updateItem = (idx, patch) => {
    setItems((prev) => prev.map((it, i) => {
      if (i !== idx) return it;
      const next = { ...it, ...patch };
      if ("quantity" in patch || "unit_value" in patch || "ipi" in patch) {
        next.total_value = Math.round((num(next.quantity) * num(next.unit_value) + num(next.ipi)) * 100) / 100;
      }
      return next;
    }));
  };

  const itemsTotal = items.reduce((s, it) => s + num(it.total_value), 0);
  const computedTotal = Math.round((itemsTotal + num(freight) + num(icmsSt) - num(discount)) * 100) / 100;

  const handleSave = () => {
    if (!supplier.trim()) { toast.error("Informe o fornecedor"); return; }
    if (!items.some((it) => it.description.trim())) { toast.error("Adicione ao menos um item com descricao"); return; }
    if (entry) { setConfirmOpen(true); return; }
    doSave();
  };

  const doSave = async () => {
    setConfirmOpen(false);
    const validItems = items.filter((it) => it.description.trim());
    setSaving(true);
    try {
      const payload = {
        client_id: clientId,
        supplier: supplier.trim(),
        supplier_cnpj: supplierCnpj.trim(),
        nf_number: nfNumber.trim(),
        nf_date: nfDate,
        items: validItems.map((it) => ({
          description: it.description.trim(),
          quantity: num(it.quantity),
          unit_value: num(it.unit_value),
          ipi: num(it.ipi),
          total_value: num(it.total_value),
        })),
        freight: num(freight),
        discount: num(discount),
        icms_st: num(icmsSt),
        total_value: totalValue !== "" && num(totalValue) > 0 ? num(totalValue) : computedTotal,
        notes,
      };
      if (entry) {
        await axios.put(`${API}/balance/entries/${entry.id}`, payload, { withCredentials: true });
        toast.success("Lancamento atualizado");
      } else {
        await axios.post(`${API}/balance/entries`, payload, { withCredentials: true });
        toast.success("Lancamento registrado");
      }
      onSaved && onSaved();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao salvar lancamento");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="balance-entry-dialog">
        <DialogHeader>
          <DialogTitle>{entry ? "Editar Lancamento" : "Lancamento Manual"}</DialogTitle>
          <DialogDescription>
            {entry ? "Altere os dados do lancamento financeiro. A edicao nao afeta o estoque." : "Registre manualmente uma entrada no controle de saldo (somente financeiro, nao afeta o estoque)."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="space-y-1 col-span-2 md:col-span-1">
              <Label className="text-xs">Fornecedor *</Label>
              <Input value={supplier} onChange={(e) => setSupplier(e.target.value)} data-testid="balance-supplier-input" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">CNPJ</Label>
              <Input value={supplierCnpj} onChange={(e) => setSupplierCnpj(e.target.value)} data-testid="balance-cnpj-input" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Numero da NF</Label>
              <Input value={nfNumber} onChange={(e) => setNfNumber(e.target.value)} data-testid="balance-nf-number-input" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Data de Emissao</Label>
              <Input type="date" value={nfDate} onChange={(e) => setNfDate(e.target.value)} data-testid="balance-nf-date-input" />
            </div>
          </div>

          <div className="border border-border rounded-lg overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="min-w-[200px]">Descricao do Produto</TableHead>
                  <TableHead className="w-[90px]">Qtd</TableHead>
                  <TableHead className="w-[110px]">Vl. Unit. (R$)</TableHead>
                  <TableHead className="w-[100px]">IPI (R$)</TableHead>
                  <TableHead className="w-[110px]">Total (R$)</TableHead>
                  <TableHead className="w-[50px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((it, idx) => (
                  <TableRow key={idx}>
                    <TableCell>
                      <Input value={it.description} onChange={(e) => updateItem(idx, { description: e.target.value })}
                        className="h-8" placeholder="Descricao" data-testid={`balance-item-desc-${idx}`} />
                    </TableCell>
                    <TableCell>
                      <Input type="number" step="0.01" value={it.quantity} onChange={(e) => updateItem(idx, { quantity: e.target.value })}
                        className="h-8 w-20" data-testid={`balance-item-qty-${idx}`} />
                    </TableCell>
                    <TableCell>
                      <Input type="number" step="0.01" value={it.unit_value} onChange={(e) => updateItem(idx, { unit_value: e.target.value })}
                        className="h-8 w-24" data-testid={`balance-item-unitvalue-${idx}`} />
                    </TableCell>
                    <TableCell>
                      <Input type="number" step="0.01" value={it.ipi} onChange={(e) => updateItem(idx, { ipi: e.target.value })}
                        className="h-8 w-20" data-testid={`balance-item-ipi-${idx}`} />
                    </TableCell>
                    <TableCell>
                      <Input type="number" step="0.01" value={it.total_value} onChange={(e) => updateItem(idx, { total_value: e.target.value })}
                        className="h-8 w-24" data-testid={`balance-item-total-${idx}`} />
                    </TableCell>
                    <TableCell>
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setItems((p) => p.filter((_, i) => i !== idx))}
                        disabled={items.length === 1} data-testid={`balance-item-remove-${idx}`}>
                        <Trash2 className="h-4 w-4 text-muted-foreground" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <Button variant="outline" size="sm" onClick={() => setItems((p) => [...p, emptyItem()])} data-testid="balance-add-item-btn">
            <Plus className="h-4 w-4 mr-1" /> Adicionar Item
          </Button>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Frete (R$)</Label>
              <Input type="number" step="0.01" value={freight} onChange={(e) => setFreight(e.target.value)} data-testid="balance-freight-input" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Desconto (R$)</Label>
              <Input type="number" step="0.01" value={discount} onChange={(e) => setDiscount(e.target.value)} data-testid="balance-discount-input" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">ICMS ST (R$)</Label>
              <Input type="number" step="0.01" value={icmsSt} onChange={(e) => setIcmsSt(e.target.value)} data-testid="balance-icmsst-input" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-semibold">Valor Total da NF (R$)</Label>
              <Input type="number" step="0.01" value={totalValue} onChange={(e) => setTotalValue(e.target.value)}
                placeholder={String(computedTotal)} data-testid="balance-total-input" />
              <p className="text-[11px] text-muted-foreground">Calculado: {fmtBRL(computedTotal)}</p>
            </div>
          </div>

          <div className="space-y-1">
            <Label className="text-xs">Observacoes</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} data-testid="balance-notes-input" />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancelar</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="balance-save-btn">
            {saving ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Salvando...</>) : entry ? "Salvar Alteracoes" : "Registrar Lancamento"}
          </Button>
        </DialogFooter>
      </DialogContent>
      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid="balance-edit-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Tem certeza que deseja alterar esta NF?</AlertDialogTitle>
            <AlertDialogDescription>
              Os dados da NF {nfNumber || "sem numero"} serao substituidos e o saldo do cliente sera recalculado.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="balance-edit-confirm-cancel-btn">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={doSave} data-testid="balance-edit-confirm-btn">Sim, alterar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
};
